import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { firestore as firestoreInstance } from "../../../firebaseConfig";
import { deriveCompanyAccessState } from "../../../utils/companyAccess";

/**
 * SubscriptionInfoCard
 * Displays subscription information prominently including:
 * - Subscription expiry date
 * - Days remaining until expiry
 * - Data usage progress
 * - Access permissions (can view data)
 *
 * This card is used to cross-reference if users can view data in the system.
 */
const SubscriptionInfoCard = ({ companyKey }) => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const firestore = firestoreInstance;

  useEffect(() => {
    if (!companyKey) {
      setLoading(false);
      return;
    }

    const fetchSubscriptionData = async () => {
      try {
        setLoading(true);
        if (!firestore) {
          setError("Firestore not initialized");
          setSubscriptionData(null);
          return;
        }
        const candidates = Array.from(
          new Set(
            [companyKey, String(companyKey || "").replace(/\//g, "-")].filter(Boolean)
          )
        );
        let resolved = null;
        for (const candidate of candidates) {
          const companySnap = await getDoc(doc(firestore, "companies", candidate));
          if (!companySnap.exists()) continue;
          const subscriptionSnap = await getDoc(
            doc(firestore, "companies", candidate, "subscriptionSnapshots", "current")
          );
          resolved = deriveCompanyAccessState({
            ...companySnap.data(),
            ...(subscriptionSnap.exists() ? subscriptionSnap.data() : {}),
          });
          break;
        }
        setSubscriptionData(resolved);
      } catch (err) {
        console.error('Error fetching subscription data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionData();
  }, [companyKey, firestore]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-20 bg-gray-200 rounded w-full mt-4"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-xl border border-red-200 p-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Subscription</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!subscriptionData) {
    return (
      <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Subscription Not Found</h3>
            <p className="text-sm text-yellow-700 mt-1">
              No subscription information available. Please contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const daysRemaining = subscriptionData.days_until_expiry ?? 0;
  const isExpiringSoon = daysRemaining > 0 && daysRemaining <= 7;
  const isExpired = !subscriptionData.is_subscription_active || daysRemaining <= 0;
  const canViewData = subscriptionData.can_read_data ?? false;
  const dataUsagePercent = subscriptionData.data_usage_percentage || 0;
  const isDataLimitReached = dataUsagePercent >= 100;

  // Determine card styling based on subscription status
  const getCardStyle = () => {
    if (isExpired || !canViewData) {
      return {
        bg: 'bg-gradient-to-br from-red-50 to-red-100',
        border: 'border-red-300',
        headerBg: 'bg-red-500',
        badge: 'bg-red-100 text-red-800 border-red-200'
      };
    }
    if (isExpiringSoon || isDataLimitReached) {
      return {
        bg: 'bg-gradient-to-br from-yellow-50 to-yellow-100',
        border: 'border-yellow-300',
        headerBg: 'bg-yellow-500',
        badge: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      };
    }
    return {
      bg: 'bg-gradient-to-br from-green-50 to-green-100',
      border: 'border-green-300',
      headerBg: 'bg-green-500',
      badge: 'bg-green-100 text-green-800 border-green-200'
    };
  };

  const cardStyle = getCardStyle();

  return (
    <div className={`rounded-xl border ${cardStyle.border} ${cardStyle.bg} shadow-lg overflow-hidden`}>
      {/* Header */}
      <div className={`${cardStyle.headerBg} px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-6 h-6 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <h3 className="text-lg font-bold text-white">Subscription Info</h3>
          </div>
          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${cardStyle.badge}`}>
            {canViewData ? 'Active' : isExpired ? 'Expired' : 'Limited Access'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Expiry Information */}
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Subscription Expires</span>
            {isExpired ? (
              <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-1 rounded">EXPIRED</span>
            ) : isExpiringSoon ? (
              <span className="text-xs font-semibold text-yellow-600 bg-yellow-100 px-2 py-1 rounded">EXPIRING SOON</span>
            ) : (
              <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-1 rounded">VALID</span>
            )}
          </div>
          <div className="flex items-baseline justify-between">
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {new Date(subscriptionData.subscription_expires_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {isExpired ? 'Subscription has expired' : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`}
              </p>
            </div>
            <svg className={`w-12 h-12 ${isExpired ? 'text-red-400' : isExpiringSoon ? 'text-yellow-400' : 'text-green-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        </div>

        {/* Data Usage */}
        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Data Usage</span>
            <span className="text-xs text-gray-600">
              {parseFloat(subscriptionData.data_used_mb ?? 0).toFixed(2)} / {parseFloat(subscriptionData.data_limit_mb ?? 0).toFixed(2)} MB
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                dataUsagePercent >= 100 ? 'bg-red-500' :
                dataUsagePercent >= 80 ? 'bg-yellow-500' :
                'bg-green-500'
              }`}
              style={{ width: `${Math.min(dataUsagePercent, 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-600">
            {dataUsagePercent.toFixed(1)}% used
            {isDataLimitReached && (
              <span className="ml-2 text-red-600 font-semibold">• Data limit reached</span>
            )}
          </p>
        </div>

        {/* Access Status */}
        <div className={`rounded-lg p-4 border ${canViewData ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center">
            {canViewData ? (
              <>
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-green-900">Full Data Access</p>
                  <p className="text-xs text-green-700 mt-0.5">You can view and manage all data</p>
                </div>
              </>
            ) : (
              <>
                <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-red-900">Limited Access</p>
                  <p className="text-xs text-red-700 mt-0.5">
                    {isExpired ? 'Subscription expired - renew to view data' :
                     isDataLimitReached ? 'Data limit reached - upgrade to continue' :
                     'Data viewing restricted'}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Write Access Info (if different from read) */}
        {!canViewData && subscriptionData.can_write_data && (
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-blue-800">
                <span className="font-semibold">Note:</span> You can still add new data, but viewing is restricted until subscription is renewed.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionInfoCard;
